<script setup lang="ts">
import axios from 'axios';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfLivePreview from '@/components/Tools/PdfLivePreview.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfPage } from '@/composables/usePdfPage';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { compare as comparePage } from '@/routes/tools/pdf';
import compareTool from '@/routes/tools/pdf/compare';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.compare.seo',
    fallbackTitleKey: 'tools.pdf.compare.title',
    fallbackTitleLabel: 'Compare PDFs',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: comparePage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.compare.title',
                title: 'Compare PDFs',
                href: comparePage.url(),
            },
        ],
    },
});

type DiffEntry = { type: 'equal' | 'insert' | 'delete'; text: string };
type Status = 'idle' | 'loading' | 'success' | 'error';

interface CompareResult {
    words_added: number;
    words_removed: number;
    words_unchanged: number;
    words_truncated: boolean;
    diff: DiffEntry[];
}

const PREVIEW_BASE_WIDTH = 420;

const fileA = ref<File | null>(null);
const fileB = ref<File | null>(null);
const status = ref<Status>('idle');
const statusMessage = ref('');
const result = ref<CompareResult | null>(null);
const previewPage = ref(1);
const previewZoomPct = ref(100);

const canvasA = ref<HTMLCanvasElement | null>(null);
const canvasB = ref<HTMLCanvasElement | null>(null);

const pdfA = usePdfPage();
const pdfB = usePdfPage();

const previewLoadingA = computed(() => pdfA.isLoading.value);
const previewLoadingB = computed(() => pdfB.isLoading.value);
const previewErrorA = computed(() => pdfA.error.value);
const previewErrorB = computed(() => pdfB.error.value);
const previewPageCountA = computed(() => pdfA.pageCount.value);
const previewPageCountB = computed(() => pdfB.pageCount.value);

const hasBothFiles = computed(() => !!fileA.value && !!fileB.value);
const showPreviews = computed(() => !!fileA.value || !!fileB.value);

const previewTotalPages = computed(() =>
    Math.max(previewPageCountA.value, previewPageCountB.value, 1),
);

const pageCountMismatch = computed(
    () =>
        hasBothFiles.value &&
        previewPageCountA.value > 0 &&
        previewPageCountB.value > 0 &&
        previewPageCountA.value !== previewPageCountB.value,
);

async function waitForCanvas(
    canvas: typeof canvasA,
): Promise<HTMLCanvasElement | null> {
    for (let attempt = 0; attempt < 12; attempt++) {
        await nextTick();

        if (canvas.value) {
            return canvas.value;
        }

        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
        });
    }

    return canvas.value;
}

async function renderPreviewPage(page: number): Promise<void> {
    const clamped = Math.min(
        previewTotalPages.value,
        Math.max(1, Math.floor(page)),
    );
    previewPage.value = clamped;

    pdfA.previewZoomPct.value = previewZoomPct.value;
    pdfB.previewZoomPct.value = previewZoomPct.value;

    const tasks: Promise<void>[] = [];

    if (canvasA.value && fileA.value && pdfA.pageCount.value > 0) {
        const pageNum = Math.min(clamped, pdfA.pageCount.value);
        tasks.push(pdfA.renderPage(canvasA.value, pageNum, PREVIEW_BASE_WIDTH));
    }

    if (canvasB.value && fileB.value && pdfB.pageCount.value > 0) {
        const pageNum = Math.min(clamped, pdfB.pageCount.value);
        tasks.push(pdfB.renderPage(canvasB.value, pageNum, PREVIEW_BASE_WIDTH));
    }

    await Promise.all(tasks);
}

async function loadPreviewA(): Promise<void> {
    if (!fileA.value) {
        pdfA.unload();

        return;
    }

    await pdfA.loadFile(fileA.value);

    if (pdfA.pageCount.value <= 0) {
        return;
    }

    previewPage.value = 1;
    await waitForCanvas(canvasA);
    await renderPreviewPage(1);
}

async function loadPreviewB(): Promise<void> {
    if (!fileB.value) {
        pdfB.unload();

        return;
    }

    await pdfB.loadFile(fileB.value);

    if (pdfB.pageCount.value <= 0) {
        return;
    }

    previewPage.value = 1;
    await waitForCanvas(canvasB);
    await renderPreviewPage(1);
}

function onFileASelected(files: File[]): void {
    fileA.value = files[0] ?? null;
    status.value = 'idle';
    result.value = null;

    if (fileA.value) {
        void loadPreviewA();
    } else {
        pdfA.unload();
    }
}

function onFileBSelected(files: File[]): void {
    fileB.value = files[0] ?? null;
    status.value = 'idle';
    result.value = null;

    if (fileB.value) {
        void loadPreviewB();
    } else {
        pdfB.unload();
    }
}

watch(previewZoomPct, () => {
    if (!showPreviews.value) {
        return;
    }

    void renderPreviewPage(previewPage.value);
});

watch(showPreviews, async (visible) => {
    if (!visible) {
        return;
    }

    await nextTick();
    await renderPreviewPage(previewPage.value);
});

async function compare(): Promise<void> {
    if (!fileA.value || !fileB.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.compare_processing',
        'Extracting and comparing text...',
    );
    result.value = null;

    const formData = new FormData();
    formData.append('file_a', fileA.value);
    formData.append('file_b', fileB.value);

    try {
        const response = await axios.post(compareTool.submit.url(), formData);
        result.value = response.data as CompareResult;
        status.value = 'success';
        statusMessage.value = '';
    } catch (err: unknown) {
        status.value = 'error';
        const e = err as {
            response?: { data: { errors: Record<string, string[]> } };
        };
        const msgs = Object.values(e.response?.data?.errors ?? {}).flat();
        statusMessage.value =
            (msgs[0] as string) ||
            t('tools.pdf.ui.compare_failed', 'Comparison failed.');
    }
}

onUnmounted(() => {
    pdfA.unload();
    pdfB.unload();
});
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-compare" />

    <ToolLayout
        :title="t('tools.pdf.compare.title', 'Compare PDFs')"
        :subtitle="t('tools.pdf.compare.description', '')"
    >
        <div class="space-y-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm sm:p-6"
                >
                    <p
                        class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{
                            t(
                                'tools.pdf.ui.compare_original_pdf',
                                'Original PDF',
                            )
                        }}
                    </p>
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf_a',
                                'Click or drag PDF A here',
                            )
                        "
                        :sublabel="
                            lineWithMax(t('tools.pdf.ui.pdf_file', 'PDF file'))
                        "
                        @update:model-value="onFileASelected"
                    />
                    <div v-if="fileA" class="mt-3">
                        <FileCard
                            :file="fileA"
                            :show-drag-handle="false"
                            @remove="
                                fileA = null;
                                pdfA.unload();
                                result = null;
                            "
                        />
                    </div>
                </div>

                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm sm:p-6"
                >
                    <p
                        class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{
                            t(
                                'tools.pdf.ui.compare_modified_pdf',
                                'Modified PDF',
                            )
                        }}
                    </p>
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf_b',
                                'Click or drag PDF B here',
                            )
                        "
                        :sublabel="
                            lineWithMax(t('tools.pdf.ui.pdf_file', 'PDF file'))
                        "
                        @update:model-value="onFileBSelected"
                    />
                    <div v-if="fileB" class="mt-3">
                        <FileCard
                            :file="fileB"
                            :show-drag-handle="false"
                            @remove="
                                fileB = null;
                                pdfB.unload();
                                result = null;
                            "
                        />
                    </div>
                </div>
            </div>

            <div v-if="showPreviews" class="space-y-3">
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {{
                        t(
                            'tools.pdf.ui.compare_preview_heading',
                            'Side-by-side preview',
                        )
                    }}
                </p>
                <p
                    v-if="pageCountMismatch"
                    class="text-xs text-amber-700 dark:text-amber-400"
                >
                    {{
                        t(
                            'tools.pdf.ui.compare_page_count_mismatch',
                            'These PDFs have different page counts ({a} vs {b}). Page navigation shows the same page number on both sides when available.',
                        )
                            .replace('{a}', String(previewPageCountA))
                            .replace('{b}', String(previewPageCountB))
                    }}
                </p>
                <div class="grid gap-4 xl:grid-cols-2">
                    <PdfLivePreview
                        v-model:zoom="previewZoomPct"
                        :has-file="!!fileA"
                        :loading="previewLoadingA"
                        :error="previewErrorA"
                        :current-page="previewPage"
                        :total-pages="previewTotalPages"
                        :empty-message="
                            t(
                                'tools.pdf.ui.compare_preview_empty_a',
                                'Upload the original PDF',
                            )
                        "
                        :rendering-message="
                            t('tools.pdf.ui.rendering', 'Rendering…')
                        "
                        @change-page="renderPreviewPage"
                    >
                        <div
                            class="flex min-h-48 items-center justify-center px-2 py-4"
                        >
                            <canvas
                                ref="canvasA"
                                class="block h-auto max-h-none rounded-md bg-white shadow-md dark:bg-gray-950"
                            />
                        </div>
                        <template #footer>
                            <p
                                v-if="fileA && !previewLoadingA"
                                class="mt-2 truncate text-center text-xs text-gray-500 dark:text-gray-400"
                                :title="fileA.name"
                            >
                                {{ fileA.name }}
                            </p>
                        </template>
                    </PdfLivePreview>

                    <PdfLivePreview
                        v-model:zoom="previewZoomPct"
                        :has-file="!!fileB"
                        :loading="previewLoadingB"
                        :error="previewErrorB"
                        :current-page="previewPage"
                        :total-pages="previewTotalPages"
                        :show-zoom="false"
                        :empty-message="
                            t(
                                'tools.pdf.ui.compare_preview_empty_b',
                                'Upload the modified PDF',
                            )
                        "
                        :rendering-message="
                            t('tools.pdf.ui.rendering', 'Rendering…')
                        "
                        @change-page="renderPreviewPage"
                    >
                        <div
                            class="flex min-h-48 items-center justify-center px-2 py-4"
                        >
                            <canvas
                                ref="canvasB"
                                class="block h-auto max-h-none rounded-md bg-white shadow-md dark:bg-gray-950"
                            />
                        </div>
                        <template #footer>
                            <p
                                v-if="fileB && !previewLoadingB"
                                class="mt-2 truncate text-center text-xs text-gray-500 dark:text-gray-400"
                                :title="fileB.name"
                            >
                                {{ fileB.name }}
                            </p>
                        </template>
                    </PdfLivePreview>
                </div>
                <p class="text-center text-xs text-gray-500 dark:text-gray-400">
                    {{
                        t(
                            'tools.pdf.ui.compare_preview_sync_hint',
                            'Use page controls on the left to browse both documents together. Run Compare to see word-level changes below.',
                        )
                    }}
                </p>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!fileA || !fileB || status === 'loading'"
                @click="compare"
            >
                {{ t('tools.pdf.ui.compare_button', 'Compare PDFs') }}
            </Button>

            <StatusMessage
                v-if="status !== 'success'"
                :status="status"
                :message="statusMessage"
            />

            <div v-if="result" class="space-y-4">
                <div
                    class="flex flex-wrap gap-4 rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <div
                        class="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2 dark:bg-green-950"
                    >
                        <span
                            class="text-lg font-bold text-green-700 dark:text-green-300"
                            >+{{ result.words_added }}</span
                        >
                        <span
                            class="text-sm text-green-700 dark:text-green-300"
                            >{{
                                t(
                                    'tools.pdf.ui.compare_words_added',
                                    'words added',
                                )
                            }}</span
                        >
                    </div>
                    <div
                        class="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2 dark:bg-red-950"
                    >
                        <span
                            class="text-lg font-bold text-red-700 dark:text-red-300"
                            >-{{ result.words_removed }}</span
                        >
                        <span class="text-sm text-red-700 dark:text-red-300">{{
                            t(
                                'tools.pdf.ui.compare_words_removed',
                                'words removed',
                            )
                        }}</span>
                    </div>
                    <div
                        class="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 dark:bg-gray-700"
                    >
                        <span
                            class="text-lg font-bold text-gray-700 dark:text-gray-300"
                            >{{ result.words_unchanged }}</span
                        >
                        <span
                            class="text-sm text-gray-500 dark:text-gray-400"
                            >{{
                                t(
                                    'tools.pdf.ui.compare_words_unchanged',
                                    'unchanged',
                                )
                            }}</span
                        >
                    </div>
                </div>

                <p
                    v-if="result.words_truncated"
                    class="text-sm text-amber-700 dark:text-amber-400"
                >
                    {{ t('tools.pdf.ui.compare_truncated_notice', '') }}
                </p>

                <div
                    class="rounded-xl border border-border bg-card p-5 shadow-sm"
                >
                    <p
                        class="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                        {{ t('tools.pdf.ui.compare_word_diff', 'Word Diff') }}
                    </p>
                    <p class="mb-3 text-xs text-gray-500 dark:text-gray-400">
                        {{
                            t(
                                'tools.pdf.ui.compare_diff_legend',
                                'Green = added in modified PDF, red strikethrough = removed from original.',
                            )
                        }}
                    </p>
                    <div
                        class="max-h-96 overflow-x-hidden overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-7 dark:border-gray-800 dark:bg-gray-900"
                    >
                        <div class="mb-4 flex flex-wrap gap-2 text-xs">
                            <span
                                class="rounded-md border border-green-300 bg-green-100 px-2 py-1 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.compare_words_added',
                                        'words added',
                                    )
                                }}
                            </span>
                            <span
                                class="rounded-md border border-red-300 bg-red-100 px-2 py-1 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                            >
                                {{
                                    t(
                                        'tools.pdf.ui.compare_words_removed',
                                        'words removed',
                                    )
                                }}
                            </span>
                        </div>
                        <div
                            class="flex flex-wrap content-start gap-1 whitespace-pre-wrap"
                        >
                            <span
                                v-for="(entry, i) in result.diff"
                                :key="i"
                                :class="{
                                    'inline-block max-w-full rounded border border-green-300 bg-green-100 px-1.5 py-0.5 break-words text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200':
                                        entry.type === 'insert',
                                    'inline-block max-w-full rounded border border-red-300 bg-red-100 px-1.5 py-0.5 break-words text-red-800 line-through dark:border-red-800 dark:bg-red-950 dark:text-red-200':
                                        entry.type === 'delete',
                                    'inline-block max-w-full px-0.5 break-words text-gray-800 dark:text-gray-200':
                                        entry.type === 'equal',
                                }"
                                >{{ entry.text }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ToolLayout>
</template>
